'use client';

import { useMemo, useRef, useState } from 'react';

import { isSSR } from '../utils/isSSR';
import { useIsomorphicLayoutEffect } from '../utils/isomorphic-layout-effect';

type UseMeasureRect = Pick<
  DOMRectReadOnly,
  'x' | 'y' | 'top' | 'left' | 'right' | 'bottom' | 'height' | 'width'
>;

const defaultState: UseMeasureRect = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
  top: 0,
  left: 0,
  bottom: 0,
  right: 0,
};

export const useMeasure = <E extends Element = Element>() => {
  const ref = useRef<E>(null);

  const [rect, setRect] = useState(defaultState);

  const observer = useMemo(() => {
    if (isSSR) {
      return null;
    }
    return new window.ResizeObserver((entries) => {
      if (entries[0]) {
        const { x, y, width, height, top, left, bottom, right } = entries[0].contentRect;
        setRect({ x, y, width, height, top, left, bottom, right });
      }
    });
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!ref.current || !observer) return;
    observer.observe(ref.current);
    return () => {
      observer.disconnect();
    };
  }, [ref.current]);

  return { ref, rect };
};
